import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React from 'react'

import { useSelector } from 'react-redux'
import { getFormValues } from 'redux-form'

import { Box, Divider, List } from '@material-ui/core'

import CollapseListItem from '~/components/shared/CollapseListItem'
import withFormName from '~/containers/withFormName'

import { useMobile } from '~/hooks/responsive'
import useModal from '~/hooks/useModal'
import useSearchHandler from '~/hooks/useSearchHandler'
import { AppState } from '~/state/store'

import { IS_RV, IS_BOAT } from '~/brands'

import {
  MakeFilterField,
  ModelFilterField,
  YearFilterField,
  AdTypeFilterField,
  LocationFilterField,
  BerthFilterField,
  AggregateTrailerMassFilterField,
  TowBallWeightFilterField,
  LengthFilterField,
  LengthFeetFilterField,
  BoatTrailerLengthFilterField,
  BoatTrailerLengthFeetFilterField,
  OdometerFilterField,
  SellerByTypeFilterField,
  PriceFilterField,
  ByDealerFilterField,
  KeywordFilterField,
  ByOfferedServiceFilterField,
  BoatDraftFilterField,
  SeriesFilterField
} from '../FilterFields'

const CategoryDialog = dynamic(
  () => import('~/components/dialogs/CategoryDialog'),
  {
    ssr: false
  }
)

type Props = {
  form: string
  change: (field: string, value: any) => void
}

const SearchFiltersCard = ({ form, change }: Props) => {
  const router = useRouter()
  const isMobile = useMobile()
  const values = useSelector(
    (state: AppState) => (getFormValues(form)(state) || {}) as any
  )

  const divider = isMobile && <Divider variant="middle" />

  const { meta } = useSearchHandler({ path: router.asPath, change })
  const { open, showModal, hideModal } = useModal()

  return (
    <List>
      <>
        {open && (
          <CategoryDialog
            in={open}
            handleConfirm={hideModal}
            hideModal={hideModal}
          />
        )}
      </>

      <CollapseListItem title="Category" disableGutters onClick={showModal} />
      {divider}
      <CollapseListItem title="Make" disableGutters>
        <MakeFilterField />
      </CollapseListItem>
      {divider}
      <CollapseListItem
        title="Model"
        disableGutters
        disabled={!values.make?.name}
      >
        <ModelFilterField values={values} />
      </CollapseListItem>
      {divider}
      <CollapseListItem
        title="Series"
        disableGutters
        disabled={!values.make?.model}
      >
        <SeriesFilterField values={values} />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Location" disableGutters>
        <LocationFilterField />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Price">
        <Box mx={1}>
          <PriceFilterField meta={meta} />
        </Box>
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Year">
        <YearFilterField meta={meta} />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Ad Type">
        <AdTypeFilterField />
      </CollapseListItem>
      {divider}
      {IS_RV && (
        <>
          <CollapseListItem title="Odometer">
            <Box mx={1}>
              <OdometerFilterField meta={meta} />
            </Box>
          </CollapseListItem>
          {divider}
        </>
      )}
      <CollapseListItem title="Seller type">
        <SellerByTypeFilterField />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Dealer" disableGutters>
        <ByDealerFilterField />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Keyword">
        <KeywordFilterField />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Seller Offered Services" disableGutters>
        <ByOfferedServiceFilterField />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Length in meters">
        <LengthFilterField meta={meta} change={change} />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Length in feets">
        <LengthFeetFilterField meta={meta} change={change} />
      </CollapseListItem>
      {divider}
      {IS_BOAT && (
        <>
          <CollapseListItem title="Boat Trailer Length, m">
            <BoatTrailerLengthFilterField meta={meta} change={change} />
          </CollapseListItem>
          {divider}
          <CollapseListItem title="Boat Trailer Length, ft">
            <BoatTrailerLengthFeetFilterField meta={meta} change={change} />
          </CollapseListItem>
          {divider}
          <CollapseListItem title="Boat Draft, m">
            <BoatDraftFilterField meta={meta} change={change} />
          </CollapseListItem>
        </>
      )}
      <CollapseListItem title="Tow Ball Weight">
        <TowBallWeightFilterField meta={meta} />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Aggregate Trailer Mass">
        <AggregateTrailerMassFilterField meta={meta} />
      </CollapseListItem>
      {divider}
      <CollapseListItem title="Sleeps/Berth Count">
        <BerthFilterField />
      </CollapseListItem>
    </List>
  )
}

export default withFormName(SearchFiltersCard)
